import React, { useState, useEffect, useMemo, useCallback } from "react";

import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { IoMdArrowBack } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { ChevronRight } from "lucide-react";
import { RiInformation2Fill } from "react-icons/ri";

import { useLoading } from "../../context/LoadingContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import config from '../../config';
import FloatingMenu from "../FloatingMenu";
import ErrorBanner from "../ui/ErrorBanner";

import TransactionStatus from "./TransactionStatus";

import {
    formatCurrency,
    sanitizeNumberInput,
    preventInvalidKeyInput
} from "../../utils/global";

import {
    fetchBankList,
    fetchDepositBankDetailList,
    fetchDepositPromotionList,
    processBankTransferDeposit
} from "../../utils/api";
import { assetPath } from "../../utils/assets";

const BankTransferDeposit = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const { token } = useAuth();
    const navigate = useNavigate();

    const [error, setError] = useState({
        message: "",
        shake: false
    });
    const [wallet, setWallet] = useState({
        balance: 0,
        status: null
    });
    const [bank, setBank] = useState({
        list: [],
        details: [],
        selected: null
    });
    const [deposit, setDeposit] = useState({
        amount: "",
        display: "",
        options: ["10000", "20000", "50000", "100000", "200000", "500000"]
    });
    const [promotion, setPromotion] = useState({
        list: [],
        selected: null,
        confirmed: null
    });
    const [file, setFile] = useState({
        name: null,
        receipt: null,
    });

    const clearError = useCallback(() => {
        setError(prev => ({...prev, message: "" }));
    }, []);

    const bankImageMap = {
        19: assetPath("/icons/bca.png"),
        20: assetPath("/icons/mandiri.png"),
        21: assetPath("/icons/bri.png"),
        22: assetPath("/icons/bni.png"),
    };

    const selectedDetail = bank.selected
        ? bank.details.find((detail) => detail.bank_id === bank.selected.id)
        : null;

    const handleReceiptUpload = useCallback((e) => {
        const uploadedFile = e.target.files[0];
        if (uploadedFile) {
            if (!uploadedFile.type.startsWith("image/")) {
                setError(prev => ({...prev, message: t("pleaseUploadImage") }));
                return;
            }
            setFile({ name: uploadedFile.name, receipt: uploadedFile });
            clearError();
        } else {
            setError(prev => ({...prev, message: t("noImageSelected") }));
        }
    }, [t, clearError]);

    const checkMinMaxDeposit = useCallback((amount) => {
        if (isNaN(amount)) {
            clearError();
            return;
        }
        const minDeposit = selectedDetail?.min_deposit?? 10000;
        const maxDeposit = selectedDetail?.max_deposit?? 10000000;

        let message = "";

        if (amount < minDeposit) {
            message = t("minDeposit", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(minDeposit, "currency", config.CURRENCY_ISO_CODE) });
        } else if (amount > maxDeposit) {
            message = t("maxDeposit", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(maxDeposit, "currency", config.CURRENCY_ISO_CODE) });
        }

        setError(prev => prev.message === message ? prev : { ...prev, message, shake: !!message });

        if (message) {
            setBank((prev) => ({ ...prev, selected: null, confirmed: null }));
            setPromotion((prev) => ({ ...prev, selected: null, confirmed: null }));
            setFile({ name: null, receipt: null });
            setTimeout(() => setError(prev => ({ ...prev, shake: false })), 500);
        }
    }, [t, clearError]);

    const handleDepositAmountChange = useCallback((input) => {
        const inputValue = input?.target ? input.target.value : input;
        const sanitizedValue = sanitizeNumberInput(String(inputValue ?? ""));

        if (!sanitizedValue) {
            setDeposit(prev => ({ ...prev, amount: "", display: "" }));
            clearError();
            return;
        }

        setDeposit(prev => ({
            ...prev,
            amount: sanitizedValue,
            display: formatCurrency(sanitizedValue, "currency", config.CURRENCY_ISO_CODE),
        }));

        const amount = Number(sanitizedValue);
        checkMinMaxDeposit(amount);
    }, [checkMinMaxDeposit, clearError]);

    const loadData = useCallback(async () => {
        setLoading(true);
        clearError();

        try {
            const [banksResponse, banksDetailsResponse, promotionResponse] = await Promise.all([
                fetchBankList(),
                fetchDepositBankDetailList(),
                fetchDepositPromotionList(),
            ]);

            setBank(prev => ({
                ...prev,
                list: banksResponse.success && Array.isArray(banksResponse.data) ? banksResponse.data : [],
                details: banksDetailsResponse.success && Array.isArray(banksDetailsResponse.data) ? banksDetailsResponse.data : []
            }));

            setPromotion(prev => ({
                ...prev,
                list: promotionResponse.success && Array.isArray(promotionResponse.data) ? promotionResponse.data : []
            }));
        } catch (error) {
            setError(prev => ({ ...prev, message: t("pleaseTryLater") }));
        } finally {
            setLoading(false);
        }
    }, [t, setLoading, clearError, navigate]);

    const handleApi = useCallback(async (params) => {
        setLoading(true);
        clearError();

        try {
            const response = await processBankTransferDeposit(params);

            if (response.success) {
                setWallet(prev => ({
                    ...prev,
                    status: {
                        status: "success",
                        method: "Bank Transfer",
                        title: t("submitSuccess"),
                        message: t("requestBeingProcessed"),
                        onPrimaryAction: () => navigate("/"),
                        primaryTitle: t("backToHome"),
                    },
                }));
            } else {
                const apiError = response.data;
                if (typeof apiError === "string") {
                    let msg = apiError;
                    if (apiError.toLowerCase().includes("promotion turnover target")) {
                        const match = apiError.match(/target (\d+)/);
                        msg = t("promotionTurnoverError", { amount: formatCurrency(match ? match[1] : "unknown") });
                    } else if (apiError.toLowerCase().includes("turnover target")) {
                        const match = apiError.match(/target (\d+)/);
                        msg = t("turnoverTargetError", { amount: formatCurrency(match ? match[1] : "unknown") });
                    } else if (apiError.toLowerCase().includes("minimum withdrawal")) {
                        const match = apiError.match(/is (\d+)/);
                        msg = t("minWithdrawalError", { amount: formatCurrency(match ? match[1] : "unknown") });
                    }

                    setError({ message: msg, shake: true });
                    setTimeout(() => setError(prev => ({...prev, shake: false })), 500);
                } else {
                    setWallet(prev => ({
                        ...prev,
                        status: {
                            method: "Bank Transfer",
                            title: t("encounteredError"),
                            message: t("unknownError"),
                            onPrimaryAction: () => {window.location.reload()},
                            primaryTitle: t("tryAgain"),
                            onSecondaryAction: () => {
                                if (window.Telegram?.WebApp) {
                                    window.Telegram.WebApp.openTelegramLink(config.TELEGRAM_URL);
                                } else {
                                    window.open(config.TELEGRAM_URL, "_blank");
                                }
                            },
                            secondaryTitle: t("contactUsForSupport"),
                        },
                    }));
                }
            }
        } catch (err) {
            setError(prev => ({ ...prev, message: t("failedToFetchLink") }));
        } finally {
            setLoading(false);
        }
    }, [t, navigate, setLoading, deposit.amount, clearError]);

    const handleSubmit = useCallback(() => {
        const currentParams = {
            promotion: promotion.confirmed,
            amount: deposit.amount,
            bank: selectedDetail,
            receipt: file.receipt ?? "skip",
        };
        handleApi(currentParams);
    }, [handleApi, promotion.confirmed, deposit.amount, selectedDetail, file.receipt]);

    const fallbackCopyTextToClipboard = useCallback((text, msg) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();

        try {
            document.execCommand("copy");
            alert(msg);
        } catch (err) {
            console.error("Fallback copy failed:", err);
        }

        document.body.removeChild(textArea);
    }, [t]);

    const handleCopyId = useCallback((id, msg) => {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard
                .writeText(id)
                .then(() => alert(msg))
                .catch((err) => {
                    console.error("Clipboard API failed, using fallback:", err);
                    fallbackCopyTextToClipboard(id, msg);
                });
        } else {
            fallbackCopyTextToClipboard(id, msg);
        }
    }, [t, fallbackCopyTextToClipboard]);
    
    useEffect(() => {
        if (error.message) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [error.message]);

    useEffect(() => {
        if (token) {
            loadData();
        }
    }, [token, loadData]);

    return (
        <section className="page-shell">
            <div className="page-header">
                <div
                    className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                    onClick={() => navigate("/deposit")}
                >
                    <IoMdArrowBack className="size-sm" />
                    <span className="text-sm">
                        {t("backToLobby")}
                    </span>
                </div>
                <button className="cursor-pointer" onClick={() => navigate("/")}>
                    <IoClose className="size-lg text-color-blue" />
                </button>
            </div>

            <ErrorBanner message={error.message} />

            <div className="px-4 pt-[30px] pb-[100px]">
                {wallet.status && <TransactionStatus {...wallet.status} />}
                {!wallet.status && (
                    <>
                        <p className="mb-3 text-sm text-center font-bold text-white">
                            {t("enterDepositAmount")}
                        </p>

                        <div className="info-banner mb-8">
                            <RiInformation2Fill className="size-[30px] text-border-yellow" />
                            <div className="flex flex-col flex-grow">
                                <span className="text-xs text-white">
                                    {t("minDeposit", {currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(bank?.selected?.min_deposit ?? 10000, "currency", config.CURRENCY_ISO_CODE)})}
                                </span>
                                <span className="text-xs text-white">
                                    {t("maxDeposit", {currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(bank?.selected?.max_deposit ?? 10000000, "currency", config.CURRENCY_ISO_CODE)})}
                                </span>
                            </div>
                        </div>

                        <div className="max-w-md my-6 text-center mx-auto w-fit">
                            <div
                                className={`text-[36px] font-bold text-white transition-transform duration-300 ${
                                    error.shake ? "animate-shake" : ""
                                }`}
                            >
                                
                                <span className="text-white/70 pl-1">
                                    {config.CURRENCY_ISO_CODE}
                                </span>

                                <input
                                    type="text"
                                    value={deposit.display || ""}
                                    onKeyDown={preventInvalidKeyInput}
                                    onChange={handleDepositAmountChange}
                                    placeholder="0"
                                    className="bg-transparent text-white text-[36px] font-bold text-right outline-none pr-2 w-auto min-w-[2ch] max-w-[10ch] md:max-w-[20ch]"
                                    style={{ width: `${(deposit.amount?.length || 1) + 1}ch` }}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3 my-8">
                            {deposit.options.map((amount, index) => (
                                <div
                                    key={index}
                                    className="relative"
                                >
                                    {amount === "50000" && (
                                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-2 py-1 text-xxs font-bold text-black uppercase rounded-full btn-gradient w-max">
                                            {t("popular")}
                                        </div>
                                    )}
                                    <div
                                        onClick={() => {
                                            handleDepositAmountChange(amount);
                                        }}
                                        className={`cursor-pointer p-2 h-[36px] text-center transition-colors rounded-sm ${amount === `${deposit.amount}` ? "btn-gradient text-black" : "bg-main-gradient text-white"}`}
                                    >
                                    <span className="text-sm font-bold">
                                            {formatCurrency(amount, "currency", config.CURRENCY_ISO_CODE)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div
                            className={`transition-all duration-300 ease-in-out ${
                                !error.message && deposit.amount
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                        >
                            <div className="mb-8">
                                <p className="mb-3 text-sm font-bold text-white">
                                    {t("selectDepositBank")}
                                </p>

                                <div className="rounded-lg bg-main-gradient transition-all duration-300 ease-in-out">
                                    {selectedDetail ? (
                                        <>
                                            <div
                                                className="container-equal-lg justify-start gap-3 cursor-pointer text-white 
                                                    border-b border-option-selected-blue 
                                                    first:rounded-t-lg 
                                                    last:rounded-b-lg 
                                                    last:border-b-0
                                                    bg-option-second-selected-blue"
                                                onClick={() => {
                                                    setBank((prev) => ({ ...prev, selected: null }));
                                                }}
                                            >
                                                <img 
                                                    src={bankImageMap[bank.selected.id]} 
                                                    alt={bank.selected.name} 
                                                    className="size-xl"
                                                />
                                                <span className="text-sm">
                                                    {bank.selected.name}
                                                </span>
                                            </div>

                                            <div className="flex flex-col p-4 animate-fadeIn">
                                                <p className="mb-3 text-sm font-bold text-white">
                                                    {t("bankDetails")}:
                                                </p>
                                                <div className="p-3 mb-3 bg-secondary rounded-lg flex flex-col">
                                                    <span className="text-sm text-color-blue">
                                                        {t("bankName")}:
                                                    </span> 
                                                    <span className="text-sm text-white">
                                                        {bank.selected.name}
                                                    </span>
                                                </div>
                                                <div className="p-3 mb-3 bg-secondary rounded-lg flex-between">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm text-color-blue">
                                                            {t("bankAccountNumber")}:
                                                        </span> 
                                                        <span className="text-sm text-white">
                                                            {selectedDetail.account_number}
                                                        </span>
                                                    </div>
                                                    <div 
                                                        className="flex-center"
                                                        onClick={() => selectedDetail.account_number && handleCopyId(selectedDetail.account_number, t("accountNumberCopied", { accountNumber: selectedDetail.account_number }))}
                                                    >
                                                        <img
                                                            src="/icons/copy1.png"
                                                            alt="Copy"
                                                            className="size-md"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="p-3 mb-3 bg-secondary rounded-lg flex-between">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm text-color-blue">
                                                            {t("bankAccountName")}:
                                                        </span> 
                                                        <span className="text-sm text-white">
                                                            {selectedDetail.account_name}
                                                        </span>
                                                    </div>
                                                    <div 
                                                        className="flex-center"
                                                        onClick={() => selectedDetail.account_name && handleCopyId(selectedDetail.account_name, t("accountNameCopied", { accountName: selectedDetail.account_name }))}
                                                    >
                                                        <img
                                                            src="/icons/copy1.png"
                                                            alt="Copy"
                                                            className="size-md"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="overflow-hidden">
                                            {bank.list.map((b) => (
                                                <div
                                                    key={b.id}
                                                    className={`container-equal-lg cursor-pointer text-white border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0 gap-3 justify-start ${bank.selected?.id === b.id ? "bg-option-second-selected-blue" : ""}`}
                                                    onClick={() => {
                                                        setBank((prev) => ({ ...prev, selected: b }));
                                                        setPromotion((prev) => ({ ...prev, selected: null, confirmed: null }));
                                                        clearError();
                                                    }}
                                                >
                                                    <img 
                                                        src={bankImageMap[b.id]} 
                                                        alt={b.name} 
                                                        className="size-lg object-contain"
                                                    />
                                                    <span className="text-sm">
                                                        {b.name}
                                                    </span>
                                                </div>
                                            ))};
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div
                            className={`transition-all duration-300 ease-in-out ${
                                !error.message && bank.selected
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                        >
                            <div className="mb-8">
                                <p className="mb-3 text-sm font-bold text-white">
                                    {t("selectPromotion")}
                                </p>

                                {promotion.list.length > 0 && (
                                    <div className="overflow-hidden">
                                        {promotion.list.map((promo) => (
                                            <div key={promo.id}>
                                                <div
                                                    className={`container-promotion-button rounded-sm bg-dropdown-bg flex-between ${promotion.selected?.id === promo.id ? "rounded-b-none" : "mb-3"}`}
                                                    onClick={() => {
                                                        setPromotion(prev => {
                                                            const newSelection = prev.selected?.id === promo.id ? null : promo;
                                                            return { ...prev, selected: newSelection, confirmed: null };
                                                        });
                                                        clearError();
                                                    }}
                                                >
                                                    <div className="flex-center gap-3">
                                                        <img
                                                            src={assetPath("/icons/gift.png")}
                                                            alt="Gift"
                                                            className="size-md"
                                                        />
                                                        <span className="text-sm text-white">
                                                            {promo.name}
                                                        </span>
                                                    </div>
                                                    <div className="flex-center gap-3">
                                                        <ChevronRight
                                                            className={`size-sm text-white animate-rotate ${promotion.selected?.id === promo.id ? "rotate-[90deg]" : ""}`}
                                                        />
                                                    </div>
                                                </div>

                                                {promotion.selected?.id === promo.id && (
                                                    <div className="flex flex-col p-4 mb-3 bg-main-gradient animate-fadeIn last:rounded-b-sm">
                                                        <div className="flex flex-col p-3 mb-3 bg-secondary rounded-lg">
                                                            <span className="text-sm text-white">
                                                                <span className="font-bold">{t("bonusAward")} </span>
                                                                : {promotion.selected.bonus_type === 1 ? promotion.selected.percentage+'%' : `${config.CURRENCY_ISO_CODE} ${formatCurrency(promotion.selected.amount, "currency", config.CURRENCY_ISO_CODE)}`}
                                                            </span>
                                                            <br />

                                                            <span className="text-sm text-white">
                                                                <span className="font-bold">{t("turnOver")} </span>
                                                                : x{promotion.selected.turnover_multiply}
                                                            </span>
                                                            <br />

                                                            <span className="text-sm text-white">
                                                                <span className="font-bold">{t("minimumDeposit")} </span>
                                                                : {config.CURRENCY_ISO_CODE} {formatCurrency(promotion.selected.min_deposit, "currency", config.CURRENCY_ISO_CODE)}
                                                            </span>
                                                            <br />

                                                            <span className="text-sm text-white">
                                                                <span className="font-bold">{t("maximumBonus")} </span>
                                                                : {promotion.selected.bonus_type === 1
                                                                ? `${config.CURRENCY_ISO_CODE} ${formatCurrency(promotion.selected.max_bonus_limit, "currency", config.CURRENCY_ISO_CODE)}`
                                                                : '-'}
                                                            </span>
                                                            <br />

                                                            <span className="text-sm text-white">
                                                                {t("promotionTNC")}
                                                            </span>
                                                        </div>

                                                        <button
                                                            className={`container-sm rounded-sm ${promotion.confirmed && promotion.confirmed !== "skip" && promotion.confirmed?.id === promo.id ? 'bg-promotion-selected' : 'btn-gradient'}`}
                                                            onClick={() => {
                                                                setPromotion(prev => {
                                                                    if (!prev.selected) return prev;
                                                                    const isSame = prev.confirmed && prev.confirmed.id === prev.selected.id;
                                                                    return { ...prev, confirmed: isSame ? null : prev.selected };
                                                                });
                                                                clearError();
                                                            }}
                                                        >
                                                            <span
                                                                className={`text-sm text-center font-bold ${promotion.confirmed && promotion.confirmed !== "skip" && promotion.confirmed?.id === promo.id ? 'text-white' : ''}`}
                                                            >
                                                                {promotion.confirmed && promotion.confirmed !== "skip" && promotion.confirmed?.id === promo.id ? t("promotionSelected") : t("selectThisPromotion")}
                                                            </span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div
                                    className={`container-promotion-button rounded-sm bg-dropdown-bg transition-all duration-300 ease-in-out flex-between 
                                        ${promotion.selected === "skip" ? "container-promotion-button-border bg-secondary border-2 border-dropdown-bg" : ""}
                                    `}
                                    onClick={() => {
                                        setPromotion(prev => ({
                                            ...prev,
                                            selected: prev.selected === "skip" ? null : "skip",
                                            confirmed: prev.confirmed === "skip" ? null : "skip"
                                        }));
                                        clearError();
                                    }}
                                >
                                    <div className="flex-center gap-3">
                                        <img
                                            src={assetPath("/icons/skip_promo.png")}
                                            alt="skip"
                                            className="size-md"
                                        />
                                        <span
                                            className={`text-sm text-white ${promotion.selected === "skip" ? "font-bold" : ""}`}
                                            >
                                            {t("skipPromotion")}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`transition-all duration-300 ease-in-out ${
                                !error.message && promotion.confirmed
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                        >
                            <div className="mb-3">
                                <p className="mb-3 text-sm font-bold text-white">
                                    {t("uploadReceipt")}
                                </p>
                                <p className="mb-3 text-sm text-white">
                                    {t("uploadReceiptDescription")}
                                </p>

                                {file.receipt && file.receipt !== "skip" ? (
                                    <div
                                        className="container-promotion-button-border bg-secondary border-2 border-dropdown-bg rounded-sm transition-all duration-300 ease-in-out flex-between" 
                                    >
                                        <div className="flex-center gap-3">
                                            <img
                                                src={assetPath("/icons/file.png")}
                                                alt="skip"
                                                className="size-md"
                                            />
                                            <span className="text-sm text-white">
                                                {file.name}
                                            </span>
                                        </div>
                                        <div 
                                            onClick={() => {
                                                setFile({ name: null, receipt: null });
                                            }}
                                        >
                                            <IoClose className="size-lg text-color-blue" />
                                        </div>
                                    </div>
                                ) : file.receipt === "skip" ? (
                                    <div
                                        className={`rounded-sm bg-dropdown-bg transition-all duration-300 ease-in-out flex-between 
                                            ${file.receipt === "skip" ? "container-promotion-button-border bg-secondary border-2 border-dropdown-bg" : "container-promotion-button "}`}
                                        onClick={() => {
                                            setFile({ name: null, receipt: null });
                                            clearError();
                                        }}
                                    >
                                        <div className="flex-center gap-3">
                                            <img
                                                src={assetPath("/icons/skip_promo.png")}
                                                alt="skip"
                                                className="size-md"
                                            />
                                            <span
                                                className={`text-sm text-white ${file.receipt === "skip" ? "font-bold" : ""}`}
                                                >
                                                {t("skipUploadReceipt")}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            capture="environment"
                                            onChange={handleReceiptUpload}
                                            className="hidden"
                                            id="file-upload"
                                        />
                                        <label
                                            htmlFor="file-upload"
                                            className="p-11 mb-3 text-white border-2 border-dropdown-bg rounded-sm cursor-pointer flex-center"
                                        >
                                            <div className="flex-col flex-center">
                                                <img
                                                    src={assetPath("/icons/file.png")}
                                                    alt="Copy"
                                                    className="size-md mb-1"
                                                />
                                                <span className="text-sm text-white mb-3">
                                                    {t("selectFile")}
                                                </span>
                                                <span className="text-sm text-white">
                                                    {t("supportFileTypes")}
                                                </span>
                                            </div>
                                        </label>

                                        <div
                                            className={`rounded-sm bg-dropdown-bg transition-all duration-300 ease-in-out flex-between 
                                                ${file.receipt === "skip" ? "container-promotion-button-border bg-secondary border-2 border-dropdown-bg" : "container-promotion-button "}`}
                                                onClick={() => {
                                                    setFile({ name: null, receipt: "skip" });
                                                    clearError();
                                                }}
                                        >
                                            <div className="flex-center gap-3">
                                                <img
                                                    src={assetPath("/icons/skip_promo.png")}
                                                    alt="skip"
                                                    className="size-md"
                                                />
                                                <span
                                                    className={`text-sm text-white ${file.receipt === "skip" ? "font-bold" : ""}`}
                                                >
                                                    {t("skipUploadReceipt")}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <button
                            className={`fixed bottom-[60px] md:bottom-0 left-0 right-0 container-lg h-[60px] text-center btn-gradient transition-all duration-300 ease-in-out ${
                                !error.message && deposit.amount && promotion.confirmed && (file.name || file.receipt) 
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                            onClick={() => {
                                handleSubmit();
                            }}
                        >
                            <span className="text-sm font-bold">
                                {t("submit")}
                            </span>
                        </button>
                    </>
                )}
            </div>
            <FloatingMenu />
        </section>
    );
};

export default BankTransferDeposit;
